import './NotificationSettingsPage.css';
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { SettingsContext } from '../SettingsContext';
import { v4 as uuidv4 } from 'uuid';
import Spinner from '../components/Spinner';
import BotTokenModal from '../components/BotTokenModal';
import { getAuthHeaders } from '../funcs/authHeaders';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const exchangesList = ['binance', 'lbank', 'htx', 'mexc', 'gate', 'bybit', 'bingx', 'bitget', 'ourbit', 'kucoin'];

const SAVE_DELAY = 3000;      // универсальный дебаунс
const FORM_SAVE_DELAY = 1000; // для форм noSpread/divergence

const emptyNoSpreadRow = () => ({ tokenName: '', buyExchange: '', sellExchange: '', minSpread: '' });
const emptyDivRow      = () => ({ tokenName: '', buyExchange: '', sellExchange: '', minSpread: '' });

const getDefaultSettings = () => ({
  exchanges: {
    binance: true, lbank: true, htx: true, mexc: true, gate: true,
    bybit: true, bingx: true, bitget: true, ourbit: true, kucoin: true
  },
  maxProfit: 100,
  minProfit: 1,
  minVolume: 300,
  tokenBlacklist: "",
  tokenWhitelist: "",
  exchangeBlacklists: Object.fromEntries(
    exchangesList.map(x => [x, { buy: "", sell: "" }])
  )
});

const NotificationSettingsPage = ({ initData }) => {
  const { webAppData } = useContext(SettingsContext);
  // const [userHasToken, setUserHasToken] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // auth
  const authData = webAppData || initData;
  const authString = typeof authData === 'string'
    ? authData
    : (authData?.initData ?? '');

  // state
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [noSpreadList, setNoSpreadList]         = useState([]);
  const [divergenceList, setDivergenceList]     = useState([]);
  const [activeTab, setActiveTab]               = useState(0);
  const [notificationSettings, setNotificationSettings] = useState(getDefaultSettings());

  // timers
  const saveTimersRef = useRef({});
  const noSpreadSaveTimerRef    = useRef(null);
  const divergenceSaveTimerRef  = useRef(null);

  // helpers
  const buildConfigToSave = useCallback(() => {
    return notificationConfig
      ? { ...notificationConfig, settings: notificationSettings }
      : { id: uuidv4(), settings: notificationSettings };
  }, [notificationConfig, notificationSettings]);

  const postConfig = useCallback(async ({ config, noSpreadList, divergenceList }) => {
    const res = await fetch(`${API_BASE_URL}/notification_config`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        initData: authString || undefined,
        config,
        noSpreadList,
        divergenceList
      }),
    });
    if (!res.ok) {
      if (res.status === 403) return null;
      throw new Error(`Ошибка ${res.status}: ${res.statusText}`);
    }
    return res.json();
  }, [authString]);

  // load config
  const fetchNotificationConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const hasFallbackToken = !!localStorage.getItem('fallback_access_token');
      if (!authString && !hasFallbackToken) {
        setLoading(false);
        const defaultConfig = { id: uuidv4(), settings: getDefaultSettings() };
        setNotificationConfig(defaultConfig);
        setNotificationSettings(defaultConfig.settings);
        setNoSpreadList(Array.from({ length: 5 }, emptyNoSpreadRow));
        setDivergenceList(Array.from({ length: 5 }, emptyDivRow));
        return;
      }

      const url = authString
        ? `${API_BASE_URL}/notification_config?initData=${encodeURIComponent(authString)}`
        : `${API_BASE_URL}/notification_config`;
      const response = await fetch(url, { headers: getAuthHeaders() });

      if (!response.ok) {
        if (response.status === 403) { setLoading(false); return; }
        throw new Error(`Ошибка ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.notification_config) {
        setNotificationConfig(data.notification_config);
        setNotificationSettings(data.notification_config.settings || getDefaultSettings());
      } else {
        const defaultConfig = { id: uuidv4(), settings: getDefaultSettings() };
        setNotificationConfig(defaultConfig);
        setNotificationSettings(defaultConfig.settings);
      }

      setNoSpreadList(
        (data.notification_no_spread && data.notification_no_spread.length)
          ? data.notification_no_spread
          : Array.from({ length: 5 }, emptyNoSpreadRow)
      );

      setDivergenceList(
        (data.notification_divergence && data.notification_divergence.length)
          ? data.notification_divergence
          : Array.from({ length: 5 }, emptyDivRow)
      );

      // setUserHasToken(!!data.user_has_token);
    } catch (err) {
      console.error('Ошибка при загрузке настроек уведомлений:', err);
      setError('Не удалось загрузить настройки уведомлений');
      const defaultConfig = { id: uuidv4(), settings: getDefaultSettings() };
      setNotificationConfig(defaultConfig);
      setNotificationSettings(defaultConfig.settings);
      setNoSpreadList(Array.from({ length: 5 }, emptyNoSpreadRow));
      setDivergenceList(Array.from({ length: 5 }, emptyDivRow));
    } finally {
      setLoading(false);
    }
  }, [authString]);

  useEffect(() => { fetchNotificationConfig(); }, [fetchNotificationConfig]);

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(clearTimeout);
      if (noSpreadSaveTimerRef.current) clearTimeout(noSpreadSaveTimerRef.current);
      if (divergenceSaveTimerRef.current) clearTimeout(divergenceSaveTimerRef.current);
    };
  }, []);

  // debounced full save (with both lists)
  const debouncedSave = (fieldId) => {
    if (saveTimersRef.current[fieldId]) clearTimeout(saveTimersRef.current[fieldId]);
    saveTimersRef.current[fieldId] = setTimeout(async () => {
      try {
        setSaving(true);
        const result = await postConfig({
          config: buildConfigToSave(),
          noSpreadList,
          divergenceList
        });
        if (result?.notification_config) {
          setNotificationConfig(result.notification_config);
          // списки сознательно не перетираем локально
        }
      } catch (e) {
        console.error('Ошибка сохранения:', e);
        setError('Не удалось сохранить настройки уведомлений');
      } finally {
        setSaving(false);
        delete saveTimersRef.current[fieldId];
      }
    }, SAVE_DELAY);
  };

  // explicit save (кнопки/blur)
  const saveNotificationConfig = async () => {
    try {
      setSaving(true);
      setError('');
      if (!authString && !localStorage.getItem('fallback_access_token')) { setError('Ошибка аутентификации: данные отсутствуют'); return; }

      const result = await postConfig({
        config: buildConfigToSave(),
        noSpreadList,
        divergenceList
      });

      if (result?.notification_config) {
        setNotificationConfig(result.notification_config);
        // по желанию можно синхронизировать списки с бэком:
        setNoSpreadList(result.notification_no_spread ?? noSpreadList);
        setDivergenceList(result.notification_divergence ?? divergenceList);
        setNotificationSettings(result.notification_config.settings || getDefaultSettings());
      }
    } catch (err) {
      console.error('Ошибка при сохранении настроек уведомлений:', err);
      setError('Не удалось сохранить настройки уведомлений');
    } finally {
      setSaving(false);
    }
  };

  // save without state update (чекбоксы) — всё равно шлём ОТРАЗУ оба списка
  const saveNotificationConfigWithoutStateUpdate = async (configToSave) => {
    try {
      setSaving(true);
      setError('');
      if (!authString && !localStorage.getItem('fallback_access_token')) { setError('Ошибка аутентификации: данные отсутствуют'); return; }

      const result = await postConfig({
        config: configToSave,
        noSpreadList,
        divergenceList
      });

      if (result?.notification_config) {
        setNotificationConfig(result.notification_config);
      }
      return result;
    } catch (err) {
      console.error('Ошибка при сохранении настроек уведомлений:', err);
      setError('Не удалось сохранить настройки уведомлений');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // handlers
  const handleExchangeChange = (e) => {
    const { name, checked } = e.target;

    setNotificationSettings((prev) => ({
      ...prev,
      exchanges: { ...prev.exchanges, [name]: checked }
    }));

    setTimeout(async () => {
      const currentChecked = checked;
      try {
        await saveNotificationConfigWithoutStateUpdate({
          ...buildConfigToSave(),
          settings: {
            ...notificationSettings,
            exchanges: { ...notificationSettings.exchanges, [name]: currentChecked }
          }
        });
      } catch (error) {
        setNotificationSettings((prev) => ({
          ...prev,
          exchanges: { ...prev.exchanges, [name]: !currentChecked }
        }));
      }
    }, 300);
  };

  const handleTokenBlacklistChange = (e) => {
    setNotificationSettings((prev) => ({ ...prev, tokenBlacklist: e.target.value }));
    debouncedSave('tokenBlacklist');
  };
  const handleTokenBlacklistBlur = () => {
    if (saveTimersRef.current['tokenBlacklist']) {
      clearTimeout(saveTimersRef.current['tokenBlacklist']);
      delete saveTimersRef.current['tokenBlacklist'];
    }
    saveNotificationConfig();
  };

  const handleMinProfitChange = (e) => {
    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
    setNotificationSettings((prev) => ({ ...prev, minProfit: value }));
    debouncedSave('minProfit');
  };
  const handleMinProfitBlur = () => {
    setNotificationSettings((prev) => ({
      ...prev,
      minProfit: prev.minProfit === '' || isNaN(prev.minProfit) ? 3 : prev.minProfit,
    }));
    if (saveTimersRef.current['minProfit']) {
      clearTimeout(saveTimersRef.current['minProfit']);
      delete saveTimersRef.current['minProfit'];
    }
    saveNotificationConfig();
  };

  const handleMaxProfitChange = (e) => {
    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
    setNotificationSettings((prev) => ({ ...prev, maxProfit: value }));
    debouncedSave('maxProfit');
  };
  const handleMaxProfitBlur = () => {
    setNotificationSettings((prev) => ({
      ...prev,
      maxProfit: prev.maxProfit === '' || isNaN(prev.maxProfit) ? 100 : prev.maxProfit,
    }));
    if (saveTimersRef.current['maxProfit']) {
      clearTimeout(saveTimersRef.current['maxProfit']);
      delete saveTimersRef.current['maxProfit'];
    }
    saveNotificationConfig();
  };

  const handleMinVolumeChange = (e) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    setNotificationSettings((prev) => ({ ...prev, minVolume: value }));
    debouncedSave('minVolume');
  };
  const handleMinVolumeBlur = () => {
    if (saveTimersRef.current['minVolume']) {
      clearTimeout(saveTimersRef.current['minVolume']);
      delete saveTimersRef.current['minVolume'];
    }
    saveNotificationConfig();
  };

  const handleExchangeBlacklistChange = (exchange, type, e) => {
    const fieldId = `blacklist_${exchange}_${type}`;
    setNotificationSettings((prev) => ({
      ...prev,
      exchangeBlacklists: {
        ...prev.exchangeBlacklists,
        [exchange]: {
          ...prev.exchangeBlacklists[exchange],
          [type]: e.target.value,
        },
      },
    }));
    debouncedSave(fieldId);
  };
  const handleExchangeBlacklistBlur = (exchange, type) => {
    const fieldId = `blacklist_${exchange}_${type}`;
    if (saveTimersRef.current[fieldId]) {
      clearTimeout(saveTimersRef.current[fieldId]);
      delete saveTimersRef.current[fieldId];
    }
    saveNotificationConfig();
  };

  const handleNoSpreadChange = (index, field, value) => {
    const newList = [...noSpreadList];
    if (!newList[index]) newList[index] = emptyNoSpreadRow();
    newList[index] = { ...newList[index], [field]: value };
    setNoSpreadList(newList);

    if (noSpreadSaveTimerRef.current) clearTimeout(noSpreadSaveTimerRef.current);
    noSpreadSaveTimerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const result = await postConfig({
          config: buildConfigToSave(),
          noSpreadList: newList,
          divergenceList
        });
        if (result?.notification_config) {
          setNotificationConfig(result.notification_config);
        }
      } catch (err) {
        console.error('Ошибка сохранения noSpread:', err);
        setError('Не удалось сохранить настройки уведомлений');
      } finally {
        setSaving(false);
      }
    }, FORM_SAVE_DELAY);
  };

  const handleDivergenceChange = (index, field, value) => {
    const newList = [...divergenceList];
    if (!newList[index]) newList[index] = emptyDivRow();
    newList[index] = { ...newList[index], [field]: value };
    setDivergenceList(newList);

    if (divergenceSaveTimerRef.current) clearTimeout(divergenceSaveTimerRef.current);
    divergenceSaveTimerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const result = await postConfig({
          config: buildConfigToSave(),
          noSpreadList,
          divergenceList: newList
        });
        if (result?.notification_config) {
          setNotificationConfig(result.notification_config);
        }
      } catch (err) {
        console.error('Ошибка сохранения divergence:', err);
        setError('Не удалось сохранить настройки уведомлений');
      } finally {
        setSaving(false);
      }
    }, FORM_SAVE_DELAY);
  };

  // dropdown data
  const spotExchanges = [
    { value: 'binance_spot', label: 'Бинанс Спот' },
    { value: 'bybit_spot', label: 'Bybit Спот' },
    { value: 'gate_spot', label: 'Gate.io Спот' },
    { value: 'htx_spot', label: 'HTX Спот' },
    { value: 'lbank_spot', label: 'Lbank Спот' },
    { value: 'mexc_spot', label: 'MEXC Спот' },
    { value: 'bingx_spot', label: 'Bingx Спот' },
    { value: 'bitget_spot', label: 'Bitget Спот' },
    { value: 'ourbit_spot', label: 'Ourbit Спот' },
    { value: 'kucoin_spot', label: 'Kucoin Спот' }
  ];
  const futuresExchanges = [
    { value: 'binance_futures', label: 'Бинанс Фьючерс' },
    { value: 'bybit_futures', label: 'Bybit Фьючерс' },
    { value: 'gate_futures', label: 'Gate.io Фьючерс' },
    { value: 'htx_futures', label: 'HTX Фьючерс' },
    { value: 'lbank_futures', label: 'Lbank Фьючерс' },
    { value: 'mexc_futures', label: 'MEXC Фьючерс' },
    { value: 'bingx_futures', label: 'Bingx Фьючерс' },
    { value: 'bitget_futures', label: 'Bitget Фьючерс' },
    { value: 'ourbit_futures', label: 'Ourbit Фьючерс' },
    { value: 'kucoin_futures', label: 'Kucoin Фьючерс' }
  ];
  const buyExchanges  = [...spotExchanges, ...futuresExchanges];
  const sellExchanges = [
    { value: 'binance', label: 'Binance' },
    { value: 'bybit', label: 'Bybit' },
    { value: 'gate', label: 'Gate.io' },
    { value: 'htx', label: 'HTX' },
    { value: 'lbank', label: 'Lbank' },
    { value: 'mexc', label: 'MEXC' },
    { value: 'bingx', label: 'Bingx' },
    { value: 'bitget', label: 'Bitget' },
    { value: 'ourbit', label: 'Ourbit' },
    { value: 'kucoin', label: 'Kucoin' }
  ];

  if (loading && !notificationConfig) {
    return (
      <div className="spinner-container">
        <Spinner />
        <p>Загрузка настроек уведомлений...</p>
      </div>
    );
  }

  return (
    <div className='SettingsPageWrapper'>
      <h2 className="page-title">Настройки уведомлений</h2>

      {/* <div className={`settingsElementWrapper`}>
        {!userHasToken && (
          <p className="info-hint"><b>Для получения уведомлений добавьте бота.</b></p>
        )}
        <button className="btn-save-template" onClick={() => setModalOpen(true)}>
          {userHasToken ? 'Изменить бота' : 'Добавить бота'}
        </button>
      </div> */}

      {(<>
        <div className="tabs">
        <button className={activeTab === 0 ? 'active' : ''} onClick={() => setActiveTab(0)}>
          Спред от 3%
        </button>
        <button className={activeTab === 1 ? 'active' : ''} onClick={() => setActiveTab(1)}>
          СХОЖДЕНИЕ
        </button>
        <button className={activeTab === 2 ? 'active' : ''} onClick={() => setActiveTab(2)}>
          РАСХОЖДЕНИЕ
        </button>
      </div>

      {activeTab === 0 && (
        <div>
          <p className="description">
            Настройте уведомления от бота в телеграмм, чтобы не пропустить ни одной важной сделки. Выберите биржи, которые будут использоваться для поиска сделок. Спред важно вводить через запятую. Например -0,5.
          </p>

          {saving && (
            <div className="saving-indicator">
              <span>Сохранение...</span>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}

          <div className='settingsElementWrapper'>
            <h3>Выберите биржи</h3>
            <div className='exchanges-list'>
              {exchangesList.map((exchange) => (
                <label key={exchange} style={{ display: 'block', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    name={exchange}
                    checked={!!notificationSettings.exchanges[exchange]}
                    onChange={handleExchangeChange}
                    disabled={saving}
                  />
                  <span className="checkbox-indicator"></span>
                  {exchange}
                </label>
              ))}
            </div>
          </div>

          <div className='settingsElementWrapper coins-list'>
            <div>
              <h3>Черный список токенов</h3>
              <input
                type="text"
                value={notificationSettings.tokenBlacklist || ''}
                onChange={handleTokenBlacklistChange}
                onBlur={handleTokenBlacklistBlur}
                placeholder="Введите токены через запятую"
                style={{ width: '100%', padding: '6px 10px' }}
              />
            </div>
          </div>

          <div className='settingsElementWrapper profits_volume'>
            <div className='profits'>
              <div>
                <h3>
                  Минимальный спред
                  <span className='minProfitSpan'> (от 3%)</span>
                </h3>
                <input
                  type="number"
                  value={notificationSettings.minProfit ?? ''}
                  onChange={handleMinProfitChange}
                  onBlur={handleMinProfitBlur}
                  min="1"
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>

              <div>
                <h3>Максимальный спред</h3>
                <input
                  type="number"
                  value={notificationSettings.maxProfit ?? ''}
                  onChange={handleMaxProfitChange}
                  onBlur={handleMaxProfitBlur}
                  min="1"
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
            </div>

            <div>
              <h3>
                Минимальный объем
                <span className='minProfitSpan'> от 300$ и выше</span>
              </h3>
              <input
                type="number"
                value={notificationSettings.minVolume ?? ''}
                onChange={handleMinVolumeChange}
                onBlur={handleMinVolumeBlur}
                min="0"
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
          </div>

          <h2>Черный список по биржам</h2>
          <div>
            {exchangesList.map((exchange) => (
              <div className='settingsElementWrapper' key={exchange}>
                <div>
                  <h3>
                    {`по ${exchange[0].toUpperCase() + exchange.slice(1)}`}
                  </h3>
                  <div>
                    <h4>Покупка</h4>
                    <input
                      type="text"
                      value={notificationSettings?.exchangeBlacklists[exchange]?.buy || ''}
                      onChange={(e) => handleExchangeBlacklistChange(exchange, 'buy', e)}
                      onBlur={() => handleExchangeBlacklistBlur(exchange, 'buy')}
                      placeholder="Введите монеты через запятую"
                      style={{ width: '100%', padding: '8px' }}
                    />
                  </div>
                  <div>
                    <h4>Продажа</h4>
                    <input
                      type="text"
                      value={notificationSettings?.exchangeBlacklists[exchange]?.sell || ''}
                      onChange={(e) => handleExchangeBlacklistChange(exchange, 'sell', e)}
                      onBlur={() => handleExchangeBlacklistBlur(exchange, 'sell')}
                      placeholder="Введите монеты через запятую"
                      style={{ width: '100%', padding: '8px' }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div>
          {saving && <div className="saving-indicator"><span>Сохранение...</span></div>}
          {error && <div className="error-message">{error}</div>}

          <p className="description">Введите монеты, за которыми хотите следить. Вы получите уведомление, когда спред станет НИЖЕ указанного вами значения.</p>

          {Array(5).fill(0).map((_, index) => (
            <div className='settingsElementWrapper' key={index}>
              <h3>{`Монета ${index + 1}`}</h3>

              {noSpreadList[index] &&
               noSpreadList[index].tokenName !== '' &&
               noSpreadList[index].minSpread !== '' &&
               noSpreadList[index].buyExchange !== '' &&
               noSpreadList[index].sellExchange !== '' && (
                <button
                  className="clear-form-btn"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      const newList = [...noSpreadList];
                      newList[index] = emptyNoSpreadRow();
                      setNoSpreadList(newList);

                      const result = await postConfig({
                        config: buildConfigToSave(),
                        noSpreadList: newList,
                        divergenceList
                      });
                      if (result?.notification_config) setNotificationConfig(result.notification_config);
                    } catch (error) {
                      console.error('Ошибка при очистке формы:', error);
                      setError('Не удалось сохранить изменения');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Очистить
                </button>
              )}

              <div className='no-spread-form'>
                <div className='input-group'>
                  <div>
                    <h4>Название монеты</h4>
                    <input
                      type="text"
                      value={noSpreadList[index]?.tokenName || ''}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleNoSpreadChange(index, 'tokenName', value);
                      }}
                      placeholder="Введите монету"
                    />
                  </div>
                  <div>
                    <h4>Спред</h4>
                    <input
                      type="text"
                      value={noSpreadList[index]?.minSpread || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace('.', ',').replace(/[^\d,-]/g, '');
                        if (value.split(',').length > 2) {
                          value = value.split(',')[0] + ',' + value.split(',').slice(1).join('');
                        }
                        handleNoSpreadChange(index, 'minSpread', value);
                      }}
                      placeholder="Введите спред"
                    />
                  </div>
                </div>

                <div className='input-group'>
                  <div>
                    <h4>Биржа покупки</h4>
                    <select
                      value={noSpreadList[index]?.buyExchange || ''}
                      onChange={(e) => handleNoSpreadChange(index, 'buyExchange', e.target.value)}
                      className={!noSpreadList[index]?.buyExchange ? 'placeholder-selected' : ''}
                    >
                      <option value="">Выберите биржу</option>
                      {buyExchanges.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <h4>Биржа продажи</h4>
                    <select
                      value={noSpreadList[index]?.sellExchange || ''}
                      onChange={(e) => handleNoSpreadChange(index, 'sellExchange', e.target.value)}
                      className={!noSpreadList[index]?.sellExchange ? 'placeholder-selected' : ''}
                    >
                      <option value="">Выберите биржу</option>
                      {sellExchanges.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 2 && (
        <div>
          {saving && <div className="saving-indicator"><span>Сохранение...</span></div>}
          {error && <div className="error-message">{error}</div>}

          <p className="description">Введите монеты, за которыми хотите следить. Вы получите уведомление, когда спред станет ВЫШЕ указанного вами значения.</p>

          {Array(5).fill(0).map((_, index) => (
            <div className='settingsElementWrapper' key={index}>
              <h3>{`Монета ${index + 1}`}</h3>

              {divergenceList[index] &&
               divergenceList[index].tokenName !== '' &&
               divergenceList[index].minSpread !== '' &&
               divergenceList[index].buyExchange !== '' &&
               divergenceList[index].sellExchange !== '' && (
                <button
                  className="clear-form-btn"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      const newList = [...divergenceList];
                      newList[index] = emptyDivRow();
                      setDivergenceList(newList);

                      const result = await postConfig({
                        config: buildConfigToSave(),
                        noSpreadList,
                        divergenceList: newList
                      });
                      if (result?.notification_config) setNotificationConfig(result.notification_config);
                    } catch (error) {
                      console.error('Ошибка при очистке формы:', error);
                      setError('Не удалось сохранить изменения');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Очистить
                </button>
              )}

              <div className='no-spread-form'>
                <div className='input-group'>
                  <div>
                    <h4>Название монеты</h4>
                    <input
                      type="text"
                      value={divergenceList[index]?.tokenName || ''}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleDivergenceChange(index, 'tokenName', value);
                      }}
                      placeholder="Введите монету"
                    />
                  </div>
                  <div>
                    <h4>Спред</h4>
                    <input
                      type="text"
                      value={divergenceList[index]?.minSpread || ''}
                      onChange={(e) => {
                        let value = e.target.value.replace('.', ',').replace(/[^\d,-]/g, '');
                        if (value.split(',').length > 2) {
                          value = value.split(',')[0] + ',' + value.split(',').slice(1).join('');
                        }
                        handleDivergenceChange(index, 'minSpread', value);
                      }}
                      placeholder="Введите спред"
                    />
                  </div>
                </div>

                <div className='input-group'>
                  <div>
                    <h4>Биржа покупки</h4>
                    <select
                      value={divergenceList[index]?.buyExchange || ''}
                      onChange={(e) => handleDivergenceChange(index, 'buyExchange', e.target.value)}
                      className={!divergenceList[index]?.buyExchange ? 'placeholder-selected' : ''}
                    >
                      <option value="">Выберите биржу</option>
                      {buyExchanges.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <h4>Биржа продажи</h4>
                    <select
                      value={divergenceList[index]?.sellExchange || ''}
                      onChange={(e) => handleDivergenceChange(index, 'sellExchange', e.target.value)}
                      className={!divergenceList[index]?.sellExchange ? 'placeholder-selected' : ''}
                    >
                      <option value="">Выберите биржу</option>
                      {sellExchanges.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}</>
      )}
    
      {/* <BotTokenModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={async () => {
            setUserHasToken(true);
            await fetchNotificationConfig();
          }}
          apiBaseUrl={API_BASE_URL}
          initDataString={authString}
        /> */}
    </div>
  );
};

export default NotificationSettingsPage;
